import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { drawerSections } from '../content';
import { useMutation } from '@apollo/client';
import { cloneDeep, isEmpty } from 'lodash';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { IMaxCreditLimitDrawerProps } from '.';
import { updateMaxCreditLimit } from './queries';
import { getError } from '@utils/graph-utils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

const MaxCreditLimitDrawer: React.FC<IMaxCreditLimitDrawerProps> = ({
  open,
  onSuccess,
  onClose,
  salesForceMaxCreditLimitDetailsInfo,
  businessPartnerId,
}) => {
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [inputsForm, setInputsForm] =
    useState<IDynamicFormWidgetSection[]>(drawerSections);

  const [maxCreditLimitAction] = useMutation(updateMaxCreditLimit());

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      const updatedSections = cloneDeep(inputsForm);
      const updatedInputs = updatedSections[0].inputs;

      if (salesForceMaxCreditLimitDetailsInfo) {
        updatedInputs.maxCreditLimitAllowedCurrency.value =
          salesForceMaxCreditLimitDetailsInfo.maxCreditLimitAllowedCurrency;
        updatedInputs.maxCreditLimitAllowed.value =
          salesForceMaxCreditLimitDetailsInfo.maxCreditLimitAllowed;
      }

      updatedInputs.maxCreditLimitAllowedCurrency.value = updatedInputs
        .maxCreditLimitAllowedCurrency.value
        ? updatedInputs.maxCreditLimitAllowedCurrency.value
        : '39735';

      setInputsForm(updatedSections);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(drawerSections, values);

    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variablesMutation = {
        entityId: businessPartnerId,
        maxCreditLimitAllowedCurrency:
          data.maxCreditLimitAllowedCurrency || null,
        maxCreditLimitAllowed: parseInt(
          data.maxCreditLimitAllowed as unknown as string
        ),
      };

      const result = await maxCreditLimitAction({
        variables: variablesMutation,
        errorPolicy: 'all',
      });

      if (isEmpty(result.errors)) {
        toast.success(
          <ToastSuccessMessage>
            Maximum Credit Limit successfully updated.
          </ToastSuccessMessage>
        );
        setTimeout(() => {
          setSubmitButtonState('success');
          onSuccess();
          onClose();
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(result)}</ToastErrorMessage>);
      }
    } catch {
      setSubmitButtonState(undefined);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title={'Modify Maximum Credit Limit'}
      onClose={() => onClose()}
      isOpen={open}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default MaxCreditLimitDrawer;
